import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  checkAccuracyTargetsFor,
  runHoldoutScore,
} from "../../lib/checkAccuracy";
import {
  useFieldLayerList,
  useSampleEvents,
  useSoilSamples,
} from "../../hooks/queries";
import {
  fetchLayerDetailCached,
  layersFromDocument,
  mergeDetail,
} from "../../lib/layerLoad";
import { useWizard } from "./wizardStore";
import { Modal } from "../../ui/Modal";

function Chalkboard() {
  return (
    <div
      className="h-24 w-44 rounded border-4 border-amber-900 bg-emerald-950 p-2"
      aria-hidden
    >
      {[0, 1, 2].map((i) => (
        <div key={i} className="mb-2 h-1.5 overflow-hidden">
          <div
            className="h-full rounded-full bg-white/80"
            style={{
              animation: `wiz-chalk 2.4s ease-out ${i * 0.5}s infinite`,
            }}
          />
        </div>
      ))}
      <div className="mt-1 text-right text-[10px] text-white/60">E = 🌽·📈</div>
    </div>
  );
}

function LessonPlanStage({
  verdict,
  onContinue,
  onExit,
}: {
  verdict: "checking" | "fail";
  onContinue: () => void;
  onExit: () => void;
}) {
  return (
    <Modal>
      <div
        className="fixed inset-0 z-top flex items-center justify-center bg-black/50"
        data-testid="wizard-lesson-plan"
      >
        <style>{`
        @keyframes wiz-bob { 0%,100% { transform: translateY(0) rotate(-4deg) } 50% { transform: translateY(-6px) rotate(4deg) } }
        @keyframes wiz-chalk { 0% { width: 0 } 60% { width: 100% } 100% { width: 100% } }
        @keyframes wiz-dots { 0% { content: '' } 25% { content: '.' } 50% { content: '..' } 75% { content: '...' } }
      `}</style>
        <div className="w-80 rounded-lg border border-line bg-surface p-4 text-center shadow-2xl">
          <div className="mx-auto flex items-end justify-center gap-2">
            <span
              className="text-4xl"
              style={{ animation: "wiz-bob 1.2s ease-in-out infinite" }}
              aria-hidden
            >
              🤖
            </span>
            {/* the chalkboard — three chalk lines write themselves, staggered, forever */}
            <Chalkboard />
          </div>
          {verdict === "checking" ? (
            <p className="mt-3 text-sm font-medium text-fg">
              Preparing lesson plan
              <span className="after:content-[''] after:[animation:wiz-dots_1.2s_steps(1)_infinite]" />
              …
            </p>
          ) : (
            <>
              <p className="mt-3 text-sm text-fg">
                This field doesn’t have the right characteristics for a good
                visual aid. I recommend you pick a different field.
              </p>
              <div className="mt-3 flex justify-center gap-2">
                <button
                  onClick={onContinue}
                  className="rounded border border-line px-3 py-1 text-xs font-medium text-fg hover:bg-hover"
                >
                  Continue Anyways
                </button>
              </div>
            </>
          )}
          <button
            onClick={onExit}
            className="mt-2 text-[11px] text-muted hover:text-fg"
            aria-label="Exit wizard"
          >
            Exit
          </button>
        </div>
      </div>
    </Modal>
  );
}

/*
 * STEP 0 — the lesson-plan loader: robot + chalkboard while the headless holdout check runs.
 * Pass = the AI map wins on P AND at least 2 other nutrients → auto-advance; fail = the honest
 * recommendation with X or "Continue Anyways".
 */
export function LessonPlanCheck({ fieldId }: { fieldId: string }) {
  const qc = useQueryClient();
  const { data: listed } = useFieldLayerList(fieldId);
  const { data: samples } = useSoilSamples(fieldId);
  const { data: events } = useSampleEvents(fieldId);
  const [verdict, setVerdict] = useState<"checking" | "fail">("checking");
  const ranRef = useRef(false);
  // ZOMBIE-PROOF: the multi-second check must be able to die. The alive flag dies with the mount
  // the RUN IDENTITY check pins the exact wizard object field and step-0 the run was started for
  // so a stale completion can never advance anyone else.
  const aliveRef = useRef(true);
  useEffect(
    () => () => {
      aliveRef.current = false;
    },
    [],
  );
  useEffect(() => {
    // events must have LOADED (undefined = still fetching) — scoring without them can use a
    // different point set than Check Accuracy itself would (old-event exclusion), flipping verdicts
    if (ranRef.current || !listed || !samples || events === undefined) return;
    ranRef.current = true;
    const startedWizard = useWizard.getState().wizard;
    const live = () => {
      if (!aliveRef.current) return false;
      const st = useWizard.getState();
      return (
        st.wizard === startedWizard && st.fieldId === fieldId && st.step === 0
      );
    };
    void (async () => {
      try {
        const stubs = layersFromDocument(listed.document).filter(
          (layer) => layer.id.startsWith("ml-") && layer.kind === "cog",
        );
        const layers = (
          await Promise.all(
            stubs.map(async (stub) => {
              try {
                const detail = await fetchLayerDetailCached(
                  qc,
                  fieldId,
                  stub.id,
                  stub.content_version,
                );
                return mergeDetail(stub, detail);
              } catch {
                return null;
              }
            }),
          )
        ).filter((layer) => layer != null);
        const targets = checkAccuracyTargetsFor(layers, samples, events);
        const wins = new Set<string>();
        for (const t of targets) {
          const stats = await runHoldoutScore(fieldId, t).catch(() => null);
          if (!live()) return;
          if (stats?.winner === "ai") wins.add(t.prop);
        }
        const pass =
          wins.has("p") && [...wins].filter((p) => p !== "p").length >= 2;
        if (!live()) return;
        if (pass) useWizard.getState().next();
        else setVerdict("fail");
      } catch {
        if (live()) setVerdict("fail");
      }
    })();
  }, [listed, samples, events, fieldId, qc]);

  return (
    <LessonPlanStage
      verdict={verdict}
      onContinue={() => useWizard.getState().next()}
      onExit={() => useWizard.getState().exit()}
    />
  );
}
